import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createReportBoard,
  duplicateReport,
  getAvailableGroups,
  getReportBoard,
  getReportTemplates,
  getReportsList,
  saveDefaultFilter,
} from "./fetch";
import { reportsKeys } from "./keys";

export const useGetReportsList = () => {
  return useQuery({
    queryKey: reportsKeys.getReportsList(),
    queryFn: () => getReportsList(),
    enabled: true,
    networkMode: "always",
    refetchOnWindowFocus: false,
  });
};

export const useGetReportBoard = ({
  boardID,
  onSuccess,
  onError,
  enabled = true,
}) => {
  return useQuery({
    queryKey: reportsKeys.getReportBoard({ boardID }),
    queryFn: () => getReportBoard({ boardID }),
    enabled,
    refetchOnMount: "always",
    networkMode: "always",
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      onSuccess && onSuccess(data);
    },
    onError: (error) => {
      onError && onError(error);
    },
  });
};

export const useCreateReportBoard = ({ onSuccess, onError }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: reportsKeys.createReportBoard(),
    mutationFn: ({ title, groupIDs, filters, isShared, duplicateBoardID }) =>
      createReportBoard({
        title,
        groupIDs,
        filters,
        isShared,
        duplicateBoardID,
      }),
    onSuccess: (resp) => {
      onSuccess(resp);
      queryClient.invalidateQueries(reportsKeys.getReportsList());
    },
    onError,
  });
};

export const useGetAvailableGroups = ({ onSuccess = () => {} }) => {
  return useQuery({
    queryKey: reportsKeys.getAvailableGroups(),
    queryFn: (boardID) => getAvailableGroups(boardID),
    enabled: true,
    networkMode: "always",
    onSuccess: onSuccess,
    refetchOnWindowFocus: false,
  });
};

export const useSaveDefaultFilters = ({ boardID, onSuccess, onError }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: reportsKeys.saveDefaultFilter({ boardID }),
    mutationFn: ({ boardID, filters }) =>
      saveDefaultFilter({ boardID, filters }),
    onSuccess: (resp) => {
      onSuccess && onSuccess(resp);
      queryClient.invalidateQueries(reportsKeys.getReportBoard({ boardID }));
    },
    onError: (err) => {
      onError && onError(err);
    },
  });
};

export const useGetReportTemplates = () => {
  return useQuery({
    queryKey: reportsKeys.getReportTemplates(),
    queryFn: () => getReportTemplates(),
    enabled: true,
    networkMode: "always",
    staleTime: Infinity,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
};

export const useDuplicateReport = ({ onSuccess, onError }) => {
  return useMutation({
    mutationKey: reportsKeys.duplicateReport(),
    mutationFn: ({
      sourceBoardID,
      title,
      groupIDs,
      copyData,
      makeReport,
      share,
    }) =>
      duplicateReport({
        sourceBoardID,
        title,
        groupIDs,
        copyData,
        makeReport,
        share,
      }),
    onSuccess: (resp) => {
      onSuccess && onSuccess(resp);
    },
    onError: (err) => {
      onError && onError(err);
    },
  });
};
